/**
 * Metric-catalog row builder — the single source of truth for the two Redshift
 * metadata catalog tables Bluey resolves metrics against.
 *
 * ## Why this module exists
 *
 * `sp_bluey_common_report` does NOT read the `base_metrics`/`derived_metrics`
 * blocks in the tool-config YAML. Those blocks are seed/validation metadata. At
 * query time the SP looks every requested metric up in two Redshift TABLES named
 * by the config's `base_metrics_catalog` / `derived_metrics_catalog` fields:
 *
 *     EXECUTE 'SELECT base_metrics_csv FROM ' || p_meta_derived_metrics_catalog ||
 *             ' WHERE UPPER(derived_metric_key) = UPPER(...)' INTO base_metrics_for_current;
 *     IF base_metrics_for_current IS NULL THEN
 *         RAISE EXCEPTION 'Derived metric not found: %', current_derived_metric;
 *
 * (DaSFintechFDA-Bluey, schema/sp_bluey_common_report.sql — the derived lookup at
 * ~L715, the base lookup at ~L773.) So a tool whose catalog tables do not exist,
 * or whose rows disagree with the YAML, fails at runtime on every call.
 *
 * The rows emitted here are therefore used in three places that must never
 * diverge, all fed from this one builder:
 *   1. `yamlAssembler.ts` — the YAML `derived_metrics` block + the `metrics`
 *      param enum (via the same passthrough synthesis).
 *   2. The Step-6 catalog tables + downloadable CSVs.
 *   3. The `POST /api/onboarding/write-catalogs` request body.
 *
 * Column names, order, and the CSV shape mirror the real FDA catalogs (e.g.
 * `schema/meta_prophecy_metrics_catalog.csv`) and the FDA "Onboarding Guide for
 * DE" Step 3, so a downloaded CSV is drop-in for the AutoFlip loader.
 */

import { BaseMetricDraft, OnboardingConfig } from './types';

// ─── Table naming ──────────────────────────────────────────────────────

/**
 * Schema that houses the catalogs. Every real catalog table on the FDA cluster
 * lives in `bluey_input` (verified: 17/17 `meta_*_catalog` tables), and the FDA
 * onboarding guide's Step 1 creates exactly `bluey_input` + `bluey_output`.
 * Overridable in the UI for a cluster that uses a different convention.
 */
export const DEFAULT_CATALOG_SCHEMA = 'bluey_input';

/**
 * Valid unquoted Redshift identifier, applied to the user-editable catalog
 * schema. Shared so the UI's inline error and the YAML assembler's warning
 * cannot disagree about what counts as valid.
 */
export const CATALOG_SCHEMA_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * Redshift's identifier limit is 127 BYTES. The longer of the two derived names
 * costs `meta_` + `_derived_metrics_catalog` = 29 chars of affix, so the
 * sanitized tool name must fit in the remainder or the two table names could
 * collide after Redshift truncates them.
 */
const MAX_IDENTIFIER_LENGTH = 127;
const LONGEST_AFFIX_LENGTH = 'meta__derived_metrics_catalog'.length;
export const MAX_CATALOG_TOOL_NAME_LENGTH = MAX_IDENTIFIER_LENGTH - LONGEST_AFFIX_LENGTH;

/**
 * Normalizes a tool name into a safe SQL identifier fragment: lowercased, every
 * run of non-`[a-z0-9_]` collapsed to a single `_`, leading/trailing `_` trimmed,
 * a leading digit prefixed (an identifier may not start with one), and truncated
 * so the derived table name stays inside Redshift's 127-byte limit.
 */
export function sanitizeToolNameForIdentifier(toolName: string): string {
  const base = (toolName || '')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');
  const prefixed = /^[0-9]/.test(base) ? `t_${base}` : base;
  // Trim any trailing `_` exposed by truncation so we never emit `meta_foo__…`.
  return prefixed.slice(0, MAX_CATALOG_TOOL_NAME_LENGTH).replace(/_+$/g, '');
}

export interface ResolvedCatalogTables {
  schema: string;
  baseTable: string;
  derivedTable: string;
  /** `schema.table` — exactly what the YAML + the SP parameter need. */
  baseQualified: string;
  derivedQualified: string;
}

/**
 * Resolves the two catalog table names from the tool name, following the FDA
 * convention `meta_<product>_metrics_catalog` / `meta_<product>_derived_metrics_catalog`.
 *
 * Falls back to `tool` when the tool name sanitizes to nothing (e.g. the user
 * has not typed one yet, or typed only punctuation), so the preview always shows
 * a syntactically valid table name instead of `meta__metrics_catalog`.
 */
export function resolveCatalogTables(config: OnboardingConfig): ResolvedCatalogTables {
  // Lowercased, like the table names: Redshift folds unquoted identifiers, so
  // `My_Schema` and `my_schema` are the same schema. The backend lowercases too —
  // if we displayed the typed casing here, the preview and the emitted YAML would
  // name `My_Schema.…` while the table actually created is `my_schema.…`.
  const schema = (
    (config.catalogSchema || DEFAULT_CATALOG_SCHEMA).trim() || DEFAULT_CATALOG_SCHEMA
  ).toLowerCase();
  const product = sanitizeToolNameForIdentifier(config.toolName) || 'tool';
  const baseTable = `meta_${product}_metrics_catalog`;
  const derivedTable = `meta_${product}_derived_metrics_catalog`;
  return {
    schema,
    baseTable,
    derivedTable,
    baseQualified: `${schema}.${baseTable}`,
    derivedQualified: `${schema}.${derivedTable}`,
  };
}

// ─── Row shapes (snake_case: these ARE the Redshift columns) ───────────

export interface BaseCatalogRow {
  metric_key: string;
  expr_sql: string;
  agg: string;
  /** NULL when the metric has no per-metric filter; the SP treats NULL/'' alike. */
  filter_condition: string | null;
}

export interface DerivedCatalogRow {
  derived_metric_key: string;
  base_metrics_csv: string;
  expr_sql: string;
  description: string;
}

export interface CatalogRows {
  base: BaseCatalogRow[];
  derived: DerivedCatalogRow[];
  /** How many of `derived` were auto-synthesized passthroughs (for the UI note). */
  synthesizedCount: number;
}

export const BASE_CATALOG_HEADERS: ReadonlyArray<keyof BaseCatalogRow> = [
  'metric_key',
  'expr_sql',
  'agg',
  'filter_condition',
];

export const DERIVED_CATALOG_HEADERS: ReadonlyArray<keyof DerivedCatalogRow> = [
  'derived_metric_key',
  'base_metrics_csv',
  'expr_sql',
  'description',
];

// ─── Passthrough synthesis ─────────────────────────────────────────────

/**
 * A humanized label for an auto-synthesized passthrough (e.g. "revenue_usd" ->
 * "Revenue Usd"), used only as the derived metric's `description`.
 */
export function humanizeMetricKey(metricKey: string): string {
  return metricKey
    .split('_')
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Splits a `base_metrics_csv` cell into trimmed, non-empty base keys. */
function splitBaseKeys(csv: string): string[] {
  return csv
    .split(',')
    .map((key) => key.trim())
    .filter((key) => key.length > 0);
}

/**
 * Bluey can only query metrics that appear in the DERIVED catalog — the SP
 * resolves the user-selected metric name against `derived_metric_key`, so a base
 * metric with no derived wrapper is unreachable. Synthesize a 1:1 passthrough
 * `ROUND(<base_key>,2)` for every EXPOSED base metric so it is individually
 * queryable — matching the established convention (the real prophecy catalog
 * exposes REVENUE/COGS/UNITS/VC/FC/EE as ROUND passthroughs even though composites
 * like GP/OI also reference those bases). A base referenced only by a composite
 * (multi-base) derived metric is NOT considered exposed on its own, so it still
 * gets a wrapper.
 *
 * A base is skipped (no passthrough) in two cases:
 *   1. `publish === false` — a dependency-only helper base. The backend's metric
 *      dependency graph flags these (`metric_inference_service` Step E): a helper
 *      such as `asp_revenue`/`asp_units` is kept in the base catalog so its derived
 *      consumer (e.g. ASP = asp_revenue/asp_units) resolves, but is deliberately
 *      NOT a standalone user-facing metric — which is exactly what a passthrough
 *      would make it. Only `false` suppresses; `publish` absent or `true` means
 *      expose (the default), so hand-added rows and any pre-`publish` producer keep
 *      the prior every-base-is-queryable behavior.
 *   2. The user already authored an explicit 1:1 passthrough of exactly that base
 *      (single-element `base_metrics_csv` equal to the base key) — we honor their
 *      expr rather than emit a duplicate.
 * Composites never skip.
 */
function synthesizePassthroughRows(
  baseMetrics: readonly BaseMetricDraft[],
  explicitDerived: readonly DerivedCatalogRow[],
): DerivedCatalogRow[] {
  const explicitlyWrapped = new Set(
    explicitDerived
      .map((d) => splitBaseKeys(d.base_metrics_csv))
      .filter((keys) => keys.length === 1)
      .map((keys) => keys[0]),
  );
  // Guard against colliding with a derived key the user already defined. The SP
  // matches on UPPER(derived_metric_key), so collision detection is
  // case-insensitive too — otherwise a user key `Revenue` and a synthesized
  // `REVENUE` would both match and the SP's SELECT..INTO would pick arbitrarily.
  const existingKeysUpper = new Set(explicitDerived.map((d) => d.derived_metric_key.toUpperCase()));

  const synthesized: DerivedCatalogRow[] = [];
  for (const base of baseMetrics) {
    // A dependency-only helper (publish=false) is intentionally not exposed as
    // its own metric; its derived consumer still resolves against the base row.
    if (base.publish === false) continue;
    if (explicitlyWrapped.has(base.metricKey)) continue;

    // Keep the emitted key unique and deterministic. Loop (not a single append)
    // so a pre-existing `<KEY>_METRIC` can't produce a duplicate derived key.
    let derivedKey = base.metricKey.toUpperCase();
    while (existingKeysUpper.has(derivedKey)) {
      derivedKey = `${derivedKey}_METRIC`;
    }
    existingKeysUpper.add(derivedKey);

    synthesized.push({
      derived_metric_key: derivedKey,
      base_metrics_csv: base.metricKey,
      // No space after the comma — matches the established config convention
      // (all real passthroughs use ROUND(<key>,2)).
      expr_sql: `ROUND(${base.metricKey},2)`,
      description: humanizeMetricKey(base.metricKey),
    });
  }

  return synthesized;
}

/**
 * Builds the exact rows that belong in the two catalog tables: the user's base
 * metrics verbatim, and their derived metrics followed by an auto-synthesized
 * `ROUND(<base>,2)` passthrough for every base metric not already 1:1-wrapped.
 *
 * This is the SAME list `yamlAssembler` emits as the YAML `derived_metrics`
 * block and unions into the `metrics` param enum — by construction, since both
 * call this function. `filter_condition` is normalized to `null` when blank so
 * the CSV/INSERT carry a real NULL rather than an empty string.
 */
export function buildCatalogRows(config: OnboardingConfig): CatalogRows {
  // Metric KEYS are trimmed. The Metrics step trims only for its duplicate check
  // and stores the raw value, so a stray `"revenue "` would otherwise flow into
  // `ROUND(revenue ,2)` and a `base_metrics_csv` that no longer matches the base
  // row (splitBaseKeys trims, the key set would not) — producing a validation
  // warning claiming a metric the user can plainly see is "not defined".
  // expr_sql / filter_condition / description are NOT trimmed: they are free SQL
  // and prose where leading whitespace is harmless and could be meaningful.
  const base: BaseCatalogRow[] = config.baseMetrics.map((m) => ({
    metric_key: m.metricKey.trim(),
    expr_sql: m.exprSql,
    agg: m.agg,
    filter_condition:
      m.filterCondition !== null && m.filterCondition.trim() !== '' ? m.filterCondition : null,
  }));

  const explicitDerived: DerivedCatalogRow[] = config.derivedMetrics.map((d) => ({
    derived_metric_key: d.derivedMetricKey.trim(),
    // Re-join on trimmed keys so the CSV cell matches the base rows exactly.
    base_metrics_csv: splitBaseKeys(d.baseMetricsCsv).join(','),
    expr_sql: d.exprSql,
    description: d.description,
  }));

  // Pass the TRIMMED base keys to synthesis so the emitted passthrough key,
  // base_metrics_csv, and ROUND(...) expression all agree with the base rows.
  const synthesized = synthesizePassthroughRows(
    config.baseMetrics.map((m) => ({ ...m, metricKey: m.metricKey.trim() })),
    explicitDerived,
  );

  return {
    base,
    derived: [...explicitDerived, ...synthesized],
    synthesizedCount: synthesized.length,
  };
}

// ─── CSV rendering ─────────────────────────────────────────────────────

/**
 * The FDA AutoFlip loader directive that prefixes every real catalog CSV in
 * DaSFintechFDA-Bluey (`schema/meta_*_catalog.csv`). The `${...}` tokens are
 * substituted by the loader pipeline, NOT by us — they are emitted literally so
 * a downloaded file is byte-compatible with the existing ones.
 */
export const AUTOFLIP_HEADER = `/**
{
  "taskType": "RedshiftAutoFlipCSV",
  "retryCount" : \${retryCount},
  "retryInterval" : \${retryInterval},
  "priority" : \${task_priority},
  "timeOut": \${timeOut}
}
**/
`;

/**
 * RFC-4180 field escaping. A field is quoted only when it contains a comma,
 * a double quote, or a newline — matching the real catalog CSVs, where
 * `measure_usd` stays bare while `"account_id IN ('40100','40830')"` and
 * `"ROUND(revenue,2)"` are quoted. Internal quotes are doubled.
 *
 * A `null` renders as an EMPTY field (not the text "null"), which Redshift's
 * COPY reads back as NULL for the optional `filter_condition` column.
 */
function escapeCsvField(value: string | null): string {
  if (value === null || value === undefined) return '';
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export interface CsvOptions {
  /** Prefix the FDA AutoFlip loader directive + a blank line. Default true. */
  autoFlipHeader?: boolean;
}

/**
 * Renders rows to a CSV string: optional AutoFlip directive, then the header
 * row, then one line per row in `headers` order. Uses `\n` line endings and a
 * trailing newline, like the checked-in catalog CSVs.
 */
export function toCsv<T>(
  rows: readonly T[],
  headers: ReadonlyArray<keyof T & string>,
  options: CsvOptions = {},
): string {
  const { autoFlipHeader = true } = options;
  const lines = [
    headers.join(','),
    // `keyof T` indexing cannot prove the value is string|null (the row
    // interfaces carry no index signature), but every catalog column is exactly
    // that, so narrow at the escape boundary rather than widening the row types.
    ...rows.map((row) =>
      headers.map((header) => escapeCsvField(row[header] as unknown as string | null)).join(','),
    ),
  ];
  const body = `${lines.join('\n')}\n`;
  // Blank line between the directive and the header row, matching the real files.
  return autoFlipHeader ? `${AUTOFLIP_HEADER}\n${body}` : body;
}

/** Convenience: the base-metrics catalog CSV for `config`. */
export function baseCatalogCsv(rows: readonly BaseCatalogRow[], options?: CsvOptions): string {
  return toCsv(rows, BASE_CATALOG_HEADERS as Array<keyof BaseCatalogRow & string>, options);
}

/** Convenience: the derived-metrics catalog CSV for `config`. */
export function derivedCatalogCsv(rows: readonly DerivedCatalogRow[], options?: CsvOptions): string {
  return toCsv(rows, DERIVED_CATALOG_HEADERS as Array<keyof DerivedCatalogRow & string>, options);
}

// ─── Pre-write validation ──────────────────────────────────────────────

/**
 * Column widths from the FDA onboarding guide's Step-3 DDL, which is what we
 * CREATE with. NOTE: the tables currently deployed on the FDA beta cluster are
 * all VARCHAR(100) and already hold over-long values (prophecy's GPPU expr_sql
 * is 111 chars) — they were created by AutoFlip's COPY, not this DDL. We follow
 * the guide's wider widths so real expressions are not truncated.
 */
export const CATALOG_COLUMN_LIMITS = {
  base: { metric_key: 100, expr_sql: 500, agg: 20, filter_condition: 2000 },
  derived: { derived_metric_key: 100, base_metrics_csv: 500, expr_sql: 2000, description: 500 },
} as const;

/**
 * Aggregations the stored procedure actually implements. Its `CASE UPPER(m.agg)`
 * handles SUM/AVG/COUNT/COUNT_DISTINCT and falls through to a SUM `ELSE` branch
 * for anything else — so MIN/MAX (offered by the Metrics step and accepted by
 * the backend model) are silently computed as SUM. Callers warn on these rather
 * than block, since it is an upstream SP limitation.
 */
/**
 * UTF-8 byte length — what Redshift's VARCHAR(n) actually counts. `TextEncoder`
 * is available in every browser the site targets and in jsdom; the `.length`
 * fallback keeps this safe in any exotic runtime (it only under-reports, which is
 * the pre-existing behavior).
 */
function byteLength(value: string): number {
  return typeof TextEncoder !== 'undefined' ? new TextEncoder().encode(value).length : value.length;
}

export const SP_SUPPORTED_AGGREGATIONS: ReadonlySet<string> = new Set([
  'SUM',
  'AVG',
  'COUNT',
  'COUNT_DISTINCT',
]);

/**
 * SQL keywords + common Redshift type names that appear as bare identifiers inside
 * a derived `expr_sql` but are NOT column/base-metric references. Used by the
 * derived-metric cross-reference (`validateCatalogRows` check #7) to avoid flagging
 * language tokens as undefined base metrics — `SQL_IDENTIFIER_RE` extracts every
 * bare word, so `CASE WHEN … END`, `IS NULL`, `CAST(x AS INTEGER)` all yield tokens
 * that must be excluded. Function names are handled separately (an identifier
 * immediately followed by `(`), so this set is only bare keywords + type names.
 *
 * Compared case-INSENSITIVELY (entries are upper-case; callers upper-case the token).
 * This is a heuristic, not an exhaustive grammar — the authoritative validity check
 * is the backend EXPLAIN dry-run. A missing entry can only cause a false WARNING
 * (never a false pass), which is dismissible.
 */
export const SQL_EXPR_KEYWORDS: ReadonlySet<string> = new Set([
  // Operators / predicates / control flow.
  'CASE',
  'WHEN',
  'THEN',
  'ELSE',
  'END',
  'AND',
  'OR',
  'NOT',
  'IS',
  'NULL',
  'IN',
  'BETWEEN',
  'LIKE',
  'ILIKE',
  'DISTINCT',
  'TRUE',
  'FALSE',
  'CAST',
  'AS',
  // Common Redshift type names (appear bare in CAST(x AS <type>) / '::' casts).
  'INTEGER',
  'INT',
  'BIGINT',
  'SMALLINT',
  'NUMERIC',
  'DECIMAL',
  'REAL',
  'FLOAT',
  'DOUBLE',
  'PRECISION',
  'VARCHAR',
  'CHAR',
  'TEXT',
  'DATE',
  'TIMESTAMP',
  'TIMESTAMPTZ',
  'TIME',
  'BOOLEAN',
  'BOOL',
]);

// ─── Version-axis guard (WARNING — see findVersionAxisErrors) ──────────

/**
 * ## What a version axis is, and why a base metric must never mention one
 *
 * A "version axis" is a column whose values are ALTERNATIVE VERSIONS of the same
 * facts (scenario / plan cycle / snapshot / vintage / revision) — the caller picks
 * one per query — rather than additive components of a total.
 *
 * The SP puts a query PARAMETER's filter in the `WHERE` clause
 * (`sp_bluey_common_report.sql:890`, `UPPER(<col>) IN (…)`) but puts a metric's
 * SQL in a `CASE` *inside* the aggregate (`:769-773`):
 *
 *     SELECT SUM(CASE WHEN scenario_type = 'Forecast' THEN total_units ELSE 0 END) AS forecast_units
 *     FROM   … WHERE UPPER(scenario) IN ('ACTUALS')
 *
 * One `WHERE`, one pass, no subqueries — so the two filters are effectively
 * AND-ed and the parameter filter dominates. And the `CASE` ends in `ELSE 0`,
 * not `ELSE NULL`, so the metric returns a confident **0** rather than erroring
 * or nulling out. Verified against beta: `forecast_units` → `0.0`, and the
 * variance metric built on it degraded to `actual - 0` — plausible, non-null,
 * and completely wrong (revenue inflated 3.96x).
 *
 * ## Both raw-SQL fields of a base row, not just the filter
 *
 * `filter_condition` is the obvious carrier, but it is NOT the only one. When
 * `filter_condition` is NULL the SP takes its *other* branch and interpolates
 * `expr_sql` raw (`:795`):
 *
 *     WHEN ''SUM'' THEN ''SUM(''||m.expr_sql||'') AS ''||QUOTE_IDENT(m.metric_key)
 *
 * so `expr_sql = "CASE WHEN scenario_type='Forecast' THEN total_units ELSE 0 END"`
 * with no `filter_condition` emits a byte-identical `SUM(CASE … ELSE 0 END)`.
 * Checking only `filter_condition` is therefore a complete bypass of this gate,
 * and both fields are checked.
 *
 * Deliberately NOT checked:
 *   - **`metric_key`** — an output alias (`QUOTE_IDENT(m.metric_key)`), never
 *     resolved against the table.
 *   - **Derived rows** (`derived_metric_key` / `expr_sql` / `base_metrics_csv`) —
 *     a derived `expr_sql` references base-metric ALIASES produced by the inner
 *     SELECT, not table columns. `actual_units - forecast_units`, or a
 *     passthrough `ROUND(snapshot_units,2)`, is CORRECT and must not be blocked;
 *     the defect is impossible there because a derived expression cannot
 *     re-filter the scan.
 */

/**
 * Tokens that name a version axis wherever in the identifier they appear.
 * Mirrors the backend's `_VERSION_AXIS_TOKENS` (`parameter_inference_service.py`)
 * so the advisory warning the user saw at the parameter step and this warning
 * cannot disagree about the vocabulary.
 *
 * Matched as whole snake_case TOKENS, never substrings — `conversion_rate` must
 * not match `version`, and `descenario` must not match `scenario`.
 */
export const VERSION_AXIS_TOKENS: readonly string[] = [
  'scenario',
  'scenarios',
  'snapshot',
  'snapshots',
  'vintage',
  'vintages',
];

/**
 * `version` / `revision` / `cycle` are deliberately NOT bare tokens above. As a
 * bare token they are a VERIFIED false-positive source: `os_version`,
 * `firmware_version`, `device_version`, `software_version`, `api_version`,
 * `schema_version`, `package_version`, `sku_version`, `model_version` and
 * `hardware_revision` are semantic software/hardware attributes and perfectly
 * good ADDITIVE dimensions — summing units across firmware versions is CORRECT.
 * The wizard's own target domain is exactly that case
 * (`fda_units_beta.dat_monthly_device_units_actuals`, DAT device units, where
 * `device_version` is an additive dimension).
 *
 * These heads only mean "version axis" when a PLANNING noun qualifies them, so
 * they are pair-gated the same way `source` / `type` already were. Mirrors the
 * backend's `_VERSION_AXIS_HEADS`.
 */
export const VERSION_AXIS_HEADS: readonly string[] = [
  'version',
  'versions',
  'revision',
  'revisions',
  'cycle',
  'cycles',
];

/**
 * Qualifiers that turn a `version` / `revision` / `cycle` head into a PLANNING
 * version axis: `scenario_version`, `plan_version`, `forecast_version`,
 * `data_version`, `budget_version`, `etl_version`, `guidance_cycle`,
 * `op_cycle`, `planning_cycle`, … Mirrors the backend's
 * `_VERSION_AXIS_QUALIFIERS`.
 */
export const VERSION_AXIS_QUALIFIERS: readonly string[] = [
  'scenario',
  'plan',
  'planning',
  'forecast',
  'budget',
  'guidance',
  'op', // op_cycle / OP1 / OP2 — the Amazon operating-plan cycles
  'data',
  'etl',
];

/**
 * Adjacent token pairs that mean "version axis" together and are NOT already
 * covered by the qualifier x head rule. `source` and `type` are far too common
 * alone to pair-gate by qualifier — `row_source` (VERIFIED additive:
 * elimination entries and source-system components, so summing across it is
 * correct) and `event_type` must stay allowed — so the version-axis ones are
 * enumerated. Mirrors the backend's `_VERSION_AXIS_TOKEN_PAIRS` exactly.
 *
 * `('as', 'of')` covers `as_of` / `as_of_date`. It has to be a PAIR rather than
 * a bare token because `as` alone is the SQL keyword (`CAST(x AS DECIMAL)`), and
 * pairs are only matched WITHIN one identifier, so a keyword `AS` can never pair
 * with a following word.
 */
export const VERSION_AXIS_TOKEN_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ['forecast', 'source'],
  ['forecast', 'type'],
  ['as', 'of'],
];

/**
 * Whole NAMES (not tokens) that are version axes. `fx_conversion` on the shipped
 * `rpt_bluey_prophecy_hc_ee` is verified ALTERNATIVE, but no token or pair rule
 * can admit it without also admitting `fx_conversion_rate` and `conversion_rate`,
 * which are RATES (numeric measures), not axes — the pair `('fx','conversion')`
 * occurs in all three names. Matching the exact whole name admits the axis and
 * leaves the `conversion_rate` guard intact. Mirrors the backend's
 * `_VERSION_AXIS_EXACT_NAMES`.
 */
export const VERSION_AXIS_EXACT_NAMES: readonly string[] = ['fx_conversion'];

/**
 * Heads that need no qualifier when they LEAD the identifier: a column called
 * exactly `version`, or one whose first token is `revision`, has no qualifier to
 * disambiguate and no other plausible reading.
 *
 * `cycle` / `cycles` are deliberately absent, so `cycle_time` (a duration
 * measure) stays allowed.
 *
 * Mirrors the backend's `_VERSION_AXIS_SOLO_HEADS`, with ONE documented
 * frontend-only widening: the backend applies this only when the head is the
 * WHOLE name (`len(tokens) == 1`), whereas we apply it whenever the head is the
 * LEADING token, so `revision_id` / `version_key` are caught too. Those read as
 * "the id OF the revision" — the revision is the entity being keyed, i.e. an
 * axis — while a trailing head reads as "the version OF the device", which is an
 * attribute. Widening in this direction is the safe one: this gate must never be
 * NARROWER than the advisory backend one, and `revision_id` was blocked by the
 * vocabulary this replaced.
 */
export const VERSION_AXIS_SOLO_HEADS: readonly string[] = [
  'version',
  'versions',
  'revision',
  'revisions',
];

const VERSION_AXIS_TOKEN_SET: ReadonlySet<string> = new Set(VERSION_AXIS_TOKENS);
const VERSION_AXIS_HEAD_SET: ReadonlySet<string> = new Set(VERSION_AXIS_HEADS);
const VERSION_AXIS_QUALIFIER_SET: ReadonlySet<string> = new Set(VERSION_AXIS_QUALIFIERS);
const VERSION_AXIS_EXACT_NAME_SET: ReadonlySet<string> = new Set(VERSION_AXIS_EXACT_NAMES);
const VERSION_AXIS_SOLO_HEAD_SET: ReadonlySet<string> = new Set(VERSION_AXIS_SOLO_HEADS);

/** Space-joined pair keys — JS Sets compare tuples by reference, strings by value. */
const VERSION_AXIS_PAIR_SET: ReadonlySet<string> = new Set(
  VERSION_AXIS_TOKEN_PAIRS.map(([first, second]) => `${first} ${second}`),
);

/**
 * A bare SQL identifier: starts with a letter/`_`/`$`, continues with those plus
 * digits. Notably EXCLUDES `.` and `"`, so `t.scenario` and `"scenario"` both
 * yield the identifier `scenario` on its own — a qualified reference is caught,
 * while an alias like `forecast.source` is read as two separate names (`forecast`,
 * `source`) and correctly does NOT pair.
 */
const SQL_IDENTIFIER_RE = /[A-Za-z_$][A-Za-z0-9_$]*/g;

/** Mirrors the backend's `_TOKEN_SPLIT_RE`, applied to a lowercased identifier. */
const TOKEN_SPLIT_RE = /[^a-z0-9]+/;

/**
 * Removes everything in a SQL string that is NOT code — single-quoted literals
 * and both comment forms — so a version-axis word appearing as DATA or PROSE is
 * never mistaken for a column reference. Without it, `category = 'scenario'` and
 * `-- pick scenario\nunits > 0` would both be falsely flagged, and a false warning
 * on this gate is noise that trains the user to dismiss the real ones.
 *
 * A single pass with explicit modes, not three regex passes, because the forms
 * nest into each other and order decides the answer:
 *   - `note = 'a -- b'` — the `--` is literal DATA; stripping comments first
 *     would eat the closing quote and swallow the rest of the statement.
 *   - `-- it's a scenario` — the `'` is comment PROSE and opens nothing;
 *     stripping literals first would treat everything to the next quote as data
 *     and hide real code.
 *   - `''` inside a literal is SQL's escaped quote, i.e. still literal content
 *     (`'it''s scenario'` is one literal, not two).
 *
 * Each removal leaves a SPACE, so `a/*x*​/b` reads as two identifiers rather than
 * being spliced into `ab`. Double-quoted identifiers are left alone — they ARE
 * code (`"scenario_type"` must still be caught).
 *
 * Unterminated forms (`'abc`, `/* abc`) consume to end-of-input: such SQL is a
 * syntax error the SP raises loudly, which is not the silent-zero failure this
 * gate exists to prevent. Block comments are not treated as nestable — the
 * conservative choice, since it leaves MORE text scannable, not less.
 */
function stripSqlLiteralsAndComments(sql: string): string {
  let out = '';
  let i = 0;
  while (i < sql.length) {
    if (sql[i] === "'") {
      i += 1;
      while (i < sql.length) {
        if (sql[i] === "'") {
          // A doubled quote is escaped content; stay inside the literal.
          if (sql[i + 1] === "'") {
            i += 2;
            continue;
          }
          i += 1;
          break;
        }
        i += 1;
      }
      out += ' ';
    } else if (sql[i] === '-' && sql[i + 1] === '-') {
      // Stop AT the newline, not past it, so the line break survives into `out`.
      i += 2;
      while (i < sql.length && sql[i] !== '\n' && sql[i] !== '\r') i += 1;
      out += ' ';
    } else if (sql[i] === '/' && sql[i + 1] === '*') {
      i += 2;
      while (i < sql.length && !(sql[i] === '*' && sql[i + 1] === '/')) i += 1;
      i += 2;
      out += ' ';
    } else {
      out += sql[i];
      i += 1;
    }
  }
  return out;
}

/**
 * True if one identifier names a version axis. Mirrors the backend's
 * `_is_version_like`, in the same precedence order: token-based, so
 * `conversion_rate` does not match `version` and `row_source` does not match
 * `forecast_source`.
 *
 * The rules, in order:
 *   1. EXACT NAME — the whole normalized name (`fx_conversion`).
 *   2. BARE TOKEN — `scenario` / `snapshot` / `vintage` anywhere in the name.
 *   3. LEADING SOLO HEAD — `version` / `revision` as the FIRST token, which
 *      needs no qualifier (`revision`, `revision_id`).
 *   4. ENUMERATED PAIR — `forecast_source`, `forecast_type`, `as_of`.
 *   5. QUALIFIER x HEAD — a planning noun immediately followed by a
 *      `version`/`revision`/`cycle` head (`data_version`, `budget_cycle`).
 *
 * Rules 3-5 are what keep `os_version`, `firmware_version`, `device_version`,
 * `model_version` and `hardware_revision` ALLOWED: a `version` head with a
 * non-planning qualifier in front of it is a product attribute, and summing an
 * additive measure across it is CORRECT.
 *
 * NOTE (deliberate judgment call): `model_version` is ALLOWED, matching the
 * backend, because `model` is not a planning qualifier. In some schemas a model
 * version genuinely IS an axis (competing forecast models over the same facts).
 * We accept that miss on purpose — the backend still warns advisorily at the
 * parameter step, and a false flag here is worse than a missed one because it is
 * noise that trains the user to dismiss the real warnings, even though the catalog
 * write it guards is create-only and unrepairable through this API.
 */
function isVersionLikeIdentifier(identifier: string): boolean {
  const tokens = identifier
    .toLowerCase()
    .split(TOKEN_SPLIT_RE)
    .filter((token) => token.length > 0);
  if (tokens.length === 0) return false;

  // 1. Whole-name match, normalized to `_` joins so `fx.conversion` and
  //    `fx__conversion` resolve to the same name the set is keyed by.
  if (VERSION_AXIS_EXACT_NAME_SET.has(tokens.join('_'))) return true;

  // 2. A bare version-axis token anywhere.
  for (const token of tokens) {
    if (VERSION_AXIS_TOKEN_SET.has(token)) return true;
  }

  // 3. A solo head in the LEADING position needs no qualifier. Checked before
  //    the pair rules so a single-token name terminates here.
  if (VERSION_AXIS_SOLO_HEAD_SET.has(tokens[0])) return true;

  // 4/5. Adjacent-pair rules — enumerated pairs, then qualifier x head.
  for (let i = 0; i + 1 < tokens.length; i++) {
    const first = tokens[i];
    const second = tokens[i + 1];
    if (VERSION_AXIS_PAIR_SET.has(`${first} ${second}`)) return true;
    if (VERSION_AXIS_QUALIFIER_SET.has(first) && VERSION_AXIS_HEAD_SET.has(second)) return true;
  }
  return false;
}

/**
 * The version-axis columns a piece of SQL references: lowercased (Redshift folds
 * unquoted identifiers, so the message must not depend on the typed casing),
 * de-duplicated, in order of first appearance — deterministic, so the message
 * text is stable.
 *
 * Empty for null/blank SQL, for SQL with no identifiers at all, and for SQL that
 * mentions these names only inside string literals or comments.
 */
export function findVersionAxisColumns(sql: string | null): string[] {
  if (!sql) return [];
  // `String.match` with a /g regex resets `lastIndex`, so the module-level
  // regex carries no state between calls.
  const identifiers = stripSqlLiteralsAndComments(sql).match(SQL_IDENTIFIER_RE) || [];
  const found: string[] = [];
  const seen = new Set<string>();
  for (const raw of identifiers) {
    const identifier = raw.toLowerCase();
    if (!seen.has(identifier) && isVersionLikeIdentifier(identifier)) {
      seen.add(identifier);
      found.push(identifier);
    }
  }
  return found;
}

/**
 * Names referenced by a derived metric's `expr_sql` that are NOT defined base
 * metrics — the identifiers a user most likely typo'd (`revenue / unts`, `CSE WHEN`).
 *
 * A derived expression runs over the base-aggregate layer, so every bare identifier
 * in it must resolve to a base-metric key (the SP aliases each base column by its
 * `metric_key`). We extract identifiers with the same machinery the version-axis
 * guard uses (`stripSqlLiteralsAndComments` + `SQL_IDENTIFIER_RE`, so string literals
 * and comments are ignored), then drop:
 *   - **function calls** — an identifier immediately followed by `(` (`ROUND`,
 *     `COALESCE`, `NULLIF`); those are SQL functions, not column references;
 *   - **SQL keywords / type names** — `SQL_EXPR_KEYWORDS` (`CASE`, `NULL`, `INTEGER`…).
 * Whatever remains is a name reference; any not in ``baseKeys`` is returned,
 * lowercased-compared but reported in first-seen original casing, de-duplicated.
 *
 * Heuristic by design (no DB) — the authoritative check is the backend EXPLAIN
 * dry-run. Comparison against ``baseKeys`` is case-SENSITIVE on the key itself
 * (Redshift aliases are folded, but the config's keys are what the SP emits), so a
 * case-mismatched reference surfaces here as "undefined" — acceptable for a warning.
 */
function findUndefinedDerivedRefs(sql: string, baseKeys: ReadonlySet<string>): string[] {
  const stripped = stripSqlLiteralsAndComments(sql);
  const found: string[] = [];
  const seen = new Set<string>();
  for (const match of stripped.matchAll(SQL_IDENTIFIER_RE)) {
    const raw = match[0];
    const end = (match.index ?? 0) + raw.length;
    // A function call: identifier immediately followed by `(` (allowing spaces).
    if (/^\s*\(/.test(stripped.slice(end))) continue;
    if (SQL_EXPR_KEYWORDS.has(raw.toUpperCase())) continue;
    if (baseKeys.has(raw)) continue;
    if (!seen.has(raw)) {
      seen.add(raw);
      found.push(raw);
    }
  }
  return found;
}

/** The raw-SQL field of a base catalog row that carried a version-axis reference. */
export type VersionAxisField = 'expr_sql' | 'filter_condition';

/**
 * The user-facing text for a flagged metric. Surfaced as a dismissible warning at
 * the catalog write step. Names the metric, the column(s), **which field to
 * edit**, the consequence, the fix, and why it still matters even as a warning.
 *
 * The mechanism sentence is field-independent on purpose: whichever field carries
 * the reference, the SP emits the same `… ELSE 0 …` inside the same aggregate, so
 * the consequence is identical however the reference arrived.
 */
export function versionAxisMessage(
  metricKey: string,
  columns: readonly string[],
  field: VersionAxisField,
): string {
  const named = columns.map((column) => `"${column}"`).join(', ');
  const single = columns.length === 1;
  const noun = single ? 'column' : 'columns';
  // Agreement matters: the plural branch already read "Those column select".
  const subject = single ? 'That column selects' : 'Those columns select';
  const where =
    field === 'expr_sql' ? 'in its SQL expression (expr_sql)' : 'in its filter (filter_condition)';
  return (
    `Base metric "${metricKey}" references the version-axis ${noun} ${named} ${where}. ` +
    `${subject} between alternative versions of the same data (actual vs forecast, ` +
    'plan cycles, snapshots) and Bluey also exposes them as query filters. The stored procedure ' +
    `evaluates ${field} inside the aggregate on the same single pass as the query's own WHERE, ` +
    `so "${metricKey}" silently returns 0 — not an error — for every query that filters ${named} ` +
    'to a different value, and any derived metric built on it becomes a confident wrong number. ' +
    `Remove ${named} from this metric's ${field} and let the query's own filter pick the version. ` +
    'This surfaces as a dismissible warning, not a hard block — but heed it: catalog rows cannot be edited or deleted once written.'
  );
}

/**
 * Every version-axis problem in ONE base metric, `expr_sql` first then
 * `filter_condition`. The single place that decides which fields are raw table
 * SQL, so any consumer that surfaces these (the catalog write step) uses one
 * coverage definition rather than re-implementing the check itself.
 *
 * Fires regardless of `agg`: no aggregation makes a version-axis reference safe.
 */
export function findVersionAxisErrorsForBaseMetric(
  metricKey: string,
  exprSql: string | null,
  filterCondition: string | null,
): string[] {
  const errors: string[] = [];
  const fields: ReadonlyArray<readonly [VersionAxisField, string | null]> = [
    ['expr_sql', exprSql],
    ['filter_condition', filterCondition],
  ];
  for (const [field, sql] of fields) {
    const columns = findVersionAxisColumns(sql);
    if (columns.length > 0) {
      errors.push(versionAxisMessage(metricKey, columns, field));
    }
  }
  return errors;
}

/**
 * Version-axis warnings: base rows whose raw SQL references a version axis.
 * Surfaced (like `validateCatalogRows`) as a dismissible warning Alert and NOT
 * consulted by the write button — the version-axis check was downgraded from a
 * hard block to a warning. The caveat it flags is still real: once a row lands in
 * the catalog table it is unrepairable through this API — `catalog_write_service`
 * is create-only (`_assert_both_empty` → 409 `CATALOG_NOT_EMPTY`) and there is no
 * update or delete endpoint, so the cost of letting a bad row through is
 * out-of-band Redshift DML, which needs its own owner sign-off. That is why the
 * warning copy is strong even though it no longer blocks.
 */
export function findVersionAxisErrors(rows: CatalogRows): string[] {
  const errors: string[] = [];
  for (const row of rows.base) {
    for (const error of findVersionAxisErrorsForBaseMetric(
      row.metric_key,
      row.expr_sql,
      row.filter_condition,
    )) {
      errors.push(error);
    }
  }
  return errors;
}

/**
 * Non-blocking correctness checks on the catalog rows. Each returned string is a
 * complete sentence for direct display. All of these describe conditions that
 * make the SP misbehave at QUERY time (silently or with an exception), so they
 * are worth surfacing before the user creates the tables.
 *
 * NOTE: warning-only by construction — the sole consumer renders these in an
 * `Alert type="warning"` and does not gate anything on them. Anything that must
 * actually be prevented belongs in `findVersionAxisErrors` instead.
 */
export function validateCatalogRows(rows: CatalogRows): string[] {
  const warnings: string[] = [];
  const baseKeys = new Set(rows.base.map((r) => r.metric_key));

  // 1. A derived metric referencing a base key that has no catalog row: the SP's
  //    base lookup returns NULL, the column is omitted from the base select, and
  //    the derived expression then references a column that does not exist.
  const danglingRefs = new Set<string>();
  for (const derived of rows.derived) {
    for (const key of splitBaseKeys(derived.base_metrics_csv)) {
      if (!baseKeys.has(key)) danglingRefs.add(key);
    }
  }
  if (danglingRefs.size > 0) {
    warnings.push(
      `These derived metrics reference base metrics that are not defined: ${[...danglingRefs].join(', ')}. ` +
        'Queries using them will fail.',
    );
  }

  // 2. Case-insensitive duplicate derived keys. The SP matches on
  //    UPPER(derived_metric_key) with SELECT ... INTO, so two rows differing only
  //    in case are both matched and one is picked arbitrarily.
  const seenUpper = new Map<string, string>();
  const collisions = new Set<string>();
  for (const derived of rows.derived) {
    const upper = derived.derived_metric_key.toUpperCase();
    const previous = seenUpper.get(upper);
    if (previous !== undefined) {
      collisions.add(`${previous} / ${derived.derived_metric_key}`);
    } else {
      seenUpper.set(upper, derived.derived_metric_key);
    }
  }
  if (collisions.size > 0) {
    warnings.push(
      `Derived metric keys collide case-insensitively (${[...collisions].join('; ')}). ` +
        'Bluey matches metric names case-insensitively and would pick one at random.',
    );
  }

  // 2b. Duplicate base metric_key rows. The SP looks a base metric up with
  //     `SELECT <expr> FROM <catalog> WHERE m.metric_key = '<key>'` INTO a single
  //     variable, so two rows sharing a key make the lookup ambiguous. Compared
  //     case-INSENSITIVELY as well: Redshift identifiers/predicates here are
  //     case-sensitive, but two keys differing only in case produce two columns
  //     the derived expressions cannot reliably distinguish.
  const seenBaseUpper = new Map<string, string>();
  const baseCollisions = new Set<string>();
  for (const row of rows.base) {
    const upper = row.metric_key.toUpperCase();
    const previous = seenBaseUpper.get(upper);
    if (previous !== undefined) {
      baseCollisions.add(previous === row.metric_key ? row.metric_key : `${previous} / ${row.metric_key}`);
    } else {
      seenBaseUpper.set(upper, row.metric_key);
    }
  }
  if (baseCollisions.size > 0) {
    warnings.push(
      `Duplicate or case-colliding base metric keys (${[...baseCollisions].join('; ')}). ` +
        'Bluey looks each base metric up by key and cannot tell these apart — rename or remove one.',
    );
  }

  // 3. Base keys where one is a substring of another. The SP dedupes collected
  //    base metrics with POSITION(<key> IN all_base_metrics) = 0, so if
  //    `asp_units` is collected first, a later `units` is treated as already
  //    present and never aggregated — its column silently goes missing.
  const shadowed = new Set<string>();
  for (const outer of baseKeys) {
    for (const inner of baseKeys) {
      if (outer !== inner && outer.includes(inner)) shadowed.add(`${inner} inside ${outer}`);
    }
  }
  if (shadowed.size > 0) {
    warnings.push(
      `One base metric name contains another (${[...shadowed].join('; ')}). ` +
        'Bluey may drop the shorter metric when both are requested — consider renaming.',
    );
  }

  // 4. Aggregations the SP does not implement (silently become SUM).
  const unsupportedAggs = new Set(
    rows.base.map((r) => r.agg).filter((agg) => !SP_SUPPORTED_AGGREGATIONS.has(agg.toUpperCase())),
  );
  if (unsupportedAggs.size > 0) {
    warnings.push(
      `Bluey's stored procedure does not implement ${[...unsupportedAggs].join(', ')} — ` +
        'those metrics will be aggregated as SUM instead.',
    );
  }

  // 5. Values that exceed the column widths we CREATE with (would be truncated).
  //    Measured in BYTES, not JS characters: Redshift VARCHAR(n) counts bytes, so
  //    a CJK/emoji filter_condition is up to 4x longer than `.length` suggests and
  //    would be silently truncated — quietly rewriting a WHERE clause, i.e. wrong
  //    numbers with no error.
  const overLong: string[] = [];
  for (const row of rows.base) {
    for (const [column, limit] of Object.entries(CATALOG_COLUMN_LIMITS.base)) {
      const value = row[column as keyof BaseCatalogRow];
      const bytes = typeof value === 'string' ? byteLength(value) : 0;
      if (bytes > limit) {
        overLong.push(`${row.metric_key}.${column} (${bytes} bytes > ${limit})`);
      }
    }
  }
  for (const row of rows.derived) {
    for (const [column, limit] of Object.entries(CATALOG_COLUMN_LIMITS.derived)) {
      const value = row[column as keyof DerivedCatalogRow];
      const bytes = typeof value === 'string' ? byteLength(value) : 0;
      if (bytes > limit) {
        overLong.push(`${row.derived_metric_key}.${column} (${bytes} bytes > ${limit})`);
      }
    }
  }
  if (overLong.length > 0) {
    warnings.push(
      `These values are longer than the catalog column allows and would be truncated: ${overLong.join('; ')}.`,
    );
  }

  // 7. Derived expr_sql identifiers that are not defined base metrics. The SP runs
  //    derived expressions against the base-aggregate layer (each base metric is a
  //    column aliased by its metric_key), so any bare name in a derived expr must
  //    correspond to a base key. A typo like `revenue / unts` (should be `units`)
  //    sails past the existing base_metrics_csv check (which only verifies the CSV
  //    *keys*) but fails at query time with "column unts does not exist". Catching it
  //    here surfaces the error immediately, before the tester button is even clicked.
  const derivedRefIssues: string[] = [];
  for (const derived of rows.derived) {
    const undefs = findUndefinedDerivedRefs(derived.expr_sql, baseKeys);
    if (undefs.length > 0) {
      derivedRefIssues.push(
        `Derived metric "${derived.derived_metric_key}" references names that are not defined base metrics: ` +
          `${undefs.join(', ')}. Queries using it will fail.`,
      );
    }
  }
  if (derivedRefIssues.length > 0) {
    warnings.push(...derivedRefIssues);
  }

  return warnings;
}
